/* =============================================================================
 * Candidate API client — docs/openapi/candidate-api.yaml (cmd/server, :8080)
 * Unauthenticated: never sends an Authorization header.
 * ========================================================================== */

import { CANDIDATE_API_BASE } from '../config.ts';
import { ApiError, createClient } from './http.ts';
import type { RegisterRequest, RoundResult } from '../types.ts';

const client = createClient({ service: 'candidate', baseUrl: CANDIDATE_API_BASE });

/**
 * The single current legal document (Terms & Conditions, which now also holds
 * the rules). NB: the backend returns 500 — not 404 — when nothing has been
 * published yet, so callers treat ANY failure as "show the fallback text".
 */
export async function getLegalDoc(): Promise<string> {
  const res = await client.get<{ content: string }>('/candidates/legal-docs');
  if (!res || typeof res.content !== 'string' || !res.content.trim()) {
    throw new ApiError(500, 'empty legal document');
  }
  return res.content;
}

export function register(body: RegisterRequest): Promise<{ id: string }> {
  return client.post<{ id: string }>('/candidates/register', body);
}

/**
 * Turn the backend's English error strings (stable per the spec) into
 * Marathi messages a candidate can act on.
 */
export function registrationErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return 'नोंदणी करताना अनपेक्षित त्रुटी आली. कृपया पुन्हा प्रयत्न करा.';
  const s = (err.serverMessage ?? '').toLowerCase();
  if (err.status === 409) {
    return 'सर्व्हरवर तात्पुरती अडचण आली. कृपया "नोंदणी पूर्ण करा" पुन्हा दाबा.';
  }
  if (err.status === 429) {
    return 'खूप नोंदणी प्रयत्न झाले आहेत. कृपया थोड्या वेळाने पुन्हा प्रयत्न करा.';
  }
  if (s.includes('bot check')) {
    return 'सुरक्षा तपासणी यशस्वी झाली नाही किंवा तिची मुदत संपली. खालील सुरक्षा तपासणी पुन्हा पूर्ण करून "नोंदणी पूर्ण करा" दाबा.';
  }
  if (s.includes('terms')) return 'कृपया अटी व नियम मान्य करा.';
  if (s.includes('school candidates must be born')) {
    return 'शालेय गटासाठी जन्मतारीख १ जानेवारी २००९ रोजी किंवा त्यानंतरची असावी.';
  }
  if (s.includes('college and open candidates must be born')) {
    return 'खुल्या गटासाठी जन्मतारीख १ जानेवारी १९८२ ते ३१ डिसेंबर २००८ दरम्यान असावी.';
  }
  if (s.includes('date of birth') || s.includes('district') || s.includes('gender') || s.includes('language')) {
    return 'जन्मतारीख, लिंग, भाषा, जिल्हा किंवा शैक्षणिक तपशील यांपैकी एखादी माहिती अवैध आहे. कृपया तपासा.';
  }
  if (s.includes('details') || s.includes('category')) {
    return 'गटाशी संबंधित तपशील अपूर्ण किंवा अवैध आहे. कृपया "तपशील" पायरीवर परत जाऊन तपासा.';
  }
  return err.message;
}

/**
 * Look up a forgotten candidate ID by phone number — POST /api/candidates/id.
 * Protected by the same Cloudflare Turnstile bot-check as registration, and
 * its own rate-limit bucket (10/hour/IP), independent of registration's.
 * Phone numbers aren't unique (e.g. a parent's phone used for multiple
 * children), so this can return more than one candidate — each with its own
 * name so the results are distinguishable.
 */
export function findId(
  phone: string,
  turnstileToken: string,
): Promise<{ candidates: { id: string; name: string }[] }> {
  return client.post<{ candidates: { id: string; name: string }[] }>(
    '/candidates/id',
    { phone, turnstile_token: turnstileToken },
  );
}

export function findIdErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return 'आयडी शोधताना अनपेक्षित त्रुटी आली. कृपया पुन्हा प्रयत्न करा.';
  const s = (err.serverMessage ?? '').toLowerCase();
  if (err.status === 404) return 'या मोबाईल क्रमांकाशी संबंधित कोणतीही नोंदणी सापडली नाही.';
  if (err.status === 429) {
    const wait = err.retryAfterSec ? ` (सुमारे ${Math.ceil(err.retryAfterSec / 60)} मिनिटांनी)` : '';
    return `खूप प्रयत्न झाले आहेत. कृपया थोड्या वेळाने पुन्हा प्रयत्न करा${wait}.`;
  }
  if (s.includes('bot check')) {
    return 'सुरक्षा तपासणी यशस्वी झाली नाही किंवा तिची मुदत संपली. खालील सुरक्षा तपासणी पुन्हा पूर्ण करून प्रयत्न करा.';
  }
  if (s.includes('phone number is required')) return 'कृपया मोबाईल क्रमांक भरा.';
  return err.message;
}

/* ----------------------------------------------------------------------------
 * Results — NO BACKEND ENDPOINT YET.
 * Proposed: GET /api/candidates/results → RoundResult[] (rounds held so far,
 * newest first, each with venues and their winners). Until it exists, the
 * results page reads apps/public/results/config.ts instead of calling this.
 * -------------------------------------------------------------------------- */
export function getResults(): Promise<RoundResult[]> {
  return client.get<RoundResult[]>('/candidates/results');
}
